import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GetPickupManifest,
  PickupManifest,
  PickupManifestRoutes,
} from 'src/app/core/models/reports/reports.model';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { PdfExportService } from 'src/app/core/services/reports/pdf-export.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  PDF_REPORT,
  PickupManifestFormGroup,
  PickupManifestGroup,
  REPORT_FILE_NAME,
} from './pickup-manifest.constants';

@Component({
  selector: 'app-pickup-manifest',
  templateUrl: './pickup-manifest.component.html',
  styleUrls: ['./pickup-manifest.component.scss'],
  standalone: false,
})
export class PickupManifestComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly data: PickupManifestFormGroup = inject(MAT_DIALOG_DATA);
  private readonly userService = inject(UserService);
  private readonly dialogRef: MatDialogRef<PickupManifestComponent> = inject(
    MatDialogRef<PickupManifestComponent>,
  );
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly onRoadOperationsService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private readonly pdfExportService: PdfExportService =
    inject(PdfExportService);

  private readonly commonUtils: CommonUtils = inject(CommonUtils);
  private readonly reportUtils: ReportsUtils = inject(ReportsUtils);
  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  pickupManifestForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  location!: string;
  routes: IOption[] = [];
  pickupManifestGroup: PickupManifestGroup[] = [];
  pickupManifestReport!: GetPickupManifest;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;
  driverUserID = '';
  reportLoop = '';
  isAllOptionSelected = true;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          if (this.data === null) {
            this.pickupManifestForm = this.fb.group({
              fromDate: [this.currDate],
              toDate: [this.currDate],
              route: null,
              reportFormat: [Options.PDF],
            });

            this.location = user?.location;

            return this.onRoadOperationsService
              .getPickupManifestRoutes(this.location)
              .pipe(
                take(1),
                map((values: PickupManifestRoutes) => {
                  const routes = values.response.dLRoutes;
                  this.routes = routes
                    .map((data) => {
                      return { label: data.route, value: data.route };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label));
                  this.routes.unshift({
                    label: STATUS_OPTIONS.ALL_SELECTED,
                    value: 'All',
                  });
                  this.pickupManifestForm
                    .get('route')
                    ?.setValue(this.routes[0].value);
                }),
              );
          } else {
            return of(false);
          }
        }),
      )
      .subscribe();
  }

  onExit() {
    this.commonUtils.openExitModal(this.pickupManifestForm.dirty);
  }

  radioChange(newValue: Options) {
    this.pickupManifestForm.get('reportFormat')?.setValue(newValue);
  }

  isDuplicate(pickupManifest: PickupManifest[]) {
    const userId = pickupManifest[0]?.userId;
    const formValue = this.pickupManifestForm.value;
    this.driverUserID = formValue.route?.value === 'All' ? 'ALL' : userId;
  }

  setReportLoop(
    pickupManifestReport: GetPickupManifest,
    _pickupManifest: PickupManifest[],
  ) {
    const formValue = this.pickupManifestForm.value;
    this.reportLoop =
      formValue.route?.value === 'All'
        ? 'ALL'
        : pickupManifestReport.response.loop;
  }

  onSubmit() {
    this.isAllOptionSelected =
      this.pickupManifestForm.get('route')?.value === 'All';

    this.onRoadOperationsService
      .getPickupManifest(
        this.location,
        this.commonUtils.getFormattedDate(
          this.pickupManifestForm.get('fromDate')?.value,
        ),
        this.commonUtils.getFormattedDate(
          this.pickupManifestForm.get('toDate')?.value,
        ),
        this.pickupManifestForm.get('route')?.value,
      )
      .pipe(
        take(1),
        map((data: GetPickupManifest) => {
          const formatType = this.pickupManifestForm.get('reportFormat')?.value;
          const pickupReport = data.response.pickupManifests;
          this.isDuplicate(pickupReport);

          if (pickupReport?.length) {
            if (formatType === Options.CSV) {
              const customHeader = [
                [
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                  'Pickup Manifest',
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                ],
              ];
              this.reportUtils.exportCSVReport(
                pickupReport,
                REPORT_FILE_NAME,
                customHeader,
              );
            }
            if (formatType === Options.PDF) {
              this.pickupManifestReport = data;
              this.pickupManifestGroup = this.groupByStop(pickupReport);
              this.setReportLoop(data, pickupReport);

              // Call the new AutoTable exporter
              const routeLabel = this.reportLoop ?? '';
              const userLabel = this.driverUserID ?? '';
              const from = this.commonUtils.getFormattedDate(
                this.pickupManifestForm.get('fromDate')?.value,
              );
              const to = this.commonUtils.getFormattedDate(
                this.pickupManifestForm.get('toDate')?.value,
              );
              const title = 'Pickup Manifest';
              const fileName = REPORT_FILE_NAME;
              const isAll = this.isAllOptionSelected;

              this.commonUtils.updateLoadingModal(true);
              this.commonUtils.lockUpdateLoadingModal(true);

              this.pdfExportService
                .exportPickupManifestPdfAutoTable(
                  this.pickupManifestReport,
                  this.pickupManifestGroup,
                  title,
                  fileName,
                  routeLabel,
                  userLabel,
                  from,
                  to,
                  isAll,
                  'l', // landscape
                  'letter',
                )
                .then(() => {
                  this.commonUtils.lockUpdateLoadingModal(false);
                  this.commonUtils.updateLoadingModal(false);
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.DOWNLOAD_SUCCESSFUL,
                  );
                })
                .catch(() => {
                  this.commonUtils.lockUpdateLoadingModal(false);
                  this.commonUtils.updateLoadingModal(false);
                });

              this.dialogRef.close();
            }
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }

  groupByStop(pickupManifest: PickupManifest[]): PickupManifestGroup[] {
    pickupManifest.sort((a, b) => {
      return Date.parse(a.time) - Date.parse(b.time);
    });

    const outputVal: PickupManifestGroup[] = [];
    pickupManifest.forEach((val) => {
      const index = outputVal.findIndex(
        (value: PickupManifestGroup) => value.stop === val.stop.toString(),
      );
      if (index > -1) {
        outputVal[index].pickupManifests.push(val);
      } else {
        outputVal.push({
          stop: val.stop.toString(),
          routeId: val.loop,
          pickupManifests: [val],
          time: val.time,
        });
      }
    });

    if (this.isAllOptionSelected) {
      outputVal.sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return Date.parse(a.time) - Date.parse(b.time);
      });
    } else {
      outputVal.sort((a, b) => Number(a.stop) - Number(b.stop));
    }

    return outputVal.map((val) => {
      const newManifest = val.pickupManifests.map((item, i) => {
        if (i == 0) {
          return item;
        } else {
          return {
            ...item,
            loop: '',
            customer: '',
            street: '',
            city: '',
            prov: '',
            postalcode: '',
            stop: '' as any,
          };
        }
      });
      return {
        stop: val.routeId || '',
        pickupManifests: newManifest,
        time: val.time,
      };
    });
  }
}
